<template>
  <div>
    <!-- Result banner -->
    <div v-if="mode === 'preview'">
      <!-- The server records a duplicate as an ERROR issue, so ok is false whenever
           duplicate_of_batchid is set. The duplicate is tested first and shown as a
           soft success: nothing in the file needs fixing. -->
      <b-alert v-if="report.duplicate_of_batchid" show variant="info" class="py-2">
        <i class="fa fa-info-circle mr-1"></i>
        {{ t("preview_duplicate", { batchid: report.duplicate_of_batchid, who: report.duplicate_of_created_by, when: when(report.duplicate_of_created_at) }) }}
        <b-button size="sm" variant="outline-info" class="ml-2" @click="$emit('showbatch', report.fingerprint)">
          <i class="fa fa-list mr-1"></i>{{ t("viewbatch_button") }}
        </b-button>
      </b-alert>
      <b-alert v-else-if="report.ok" show variant="success" class="py-2">
        <i class="fa fa-check-circle mr-1"></i>{{ t("preview_ok") }}
      </b-alert>
      <b-alert v-else show variant="danger" class="py-2">
        <i class="fa fa-exclamation-circle mr-1"></i>{{ t("preview_failed") }}
      </b-alert>

      <b-alert v-if="report.provisional_range_start !== null && report.provisional_range_start !== undefined" show
        variant="secondary" class="py-2">
        <i class="fa fa-sort-numeric-asc mr-1"></i>
        {{ t("range_banner", { counter: counterlabel, start: report.provisional_range_start, end: report.provisional_range_end }) }}
        <span v-if="report.provisional_note"> {{ report.provisional_note }}</span>
      </b-alert>

      <!-- Dummy payments (#153): BL Entry and BL Tran Ref are minted on load from
           l Last Entry, so the preview shows today's next numbers only. -->
      <b-alert v-if="report.assigned" show variant="secondary" class="py-2">
        <i class="fa fa-sort-numeric-asc mr-1"></i>
        {{ t("assign_banner_preview", { count: report.assigned.count, estart: report.assigned.entry_start, eend: report.assigned.entry_end, rstart: report.assigned.ref_start, rend: report.assigned.ref_end }) }}
        <span v-if="report.assigned.note"> {{ report.assigned.note }}</span>
      </b-alert>
    </div>

    <div v-else>
      <b-alert show variant="success" class="py-2">
        <i class="fa fa-check-circle mr-1"></i>
        {{ t("commit_ok", { batchid: report.batchid, rows: report.rows_inserted }) }}
        <span v-if="report.range_start !== null && report.range_start !== undefined">
          {{ t("commit_range", { counter: counterlabel, start: report.range_start, end: report.range_end, next: report.sequence_set_to }) }}
        </span>
        <span v-if="report.assigned">
          {{ t("assign_banner_commit", { count: report.assigned.count, estart: report.assigned.entry_start, eend: report.assigned.entry_end, rstart: report.assigned.ref_start, rend: report.assigned.ref_end }) }}
        </span>
      </b-alert>
    </div>

    <b-alert :show="hasstopwarning" variant="danger" class="py-2">
      <i class="fa fa-ban mr-1"></i>{{ t("stop_warning") }}
    </b-alert>

    <!-- Per-file grids -->
    <b-tabs v-model="activefile" card small class="mb-3">
      <b-tab v-for="g in filegrids" :key="g.file">
        <template #title>
          {{ slotname(g.file) }} <b-badge variant="secondary">{{ g.report.rows }}</b-badge>
        </template>
        <div class="small mb-1">
          {{ t("file_summary", { rows: g.report.rows, kept: (g.report.columns || []).length, stripped: (g.report.columns_stripped || []).length, trailing: g.report.trailing_empty_rows_stripped || 0, subs: g.report.substitutions || 0 }) }}
        </div>
        <div v-if="(g.report.columns_stripped || []).length" class="small mb-1">
          <span class="text-muted">{{ t("file_stripped_hint", { list: "" }) }}</span>
          <b-badge v-for="c in g.report.columns_stripped" :key="c" variant="light" class="text-muted mr-1">{{ c }}</b-badge>
        </div>
        <div v-if="g.report.rows_truncated" class="small text-warning mb-1">
          {{ t("file_truncated", { shown: g.items.length, total: g.report.rows_total }) }}
        </div>
        <b-table small striped sticky-header="480px" :dark="$store.state.darkMode" :fields="g.fields" :items="g.items"
          :tbody-tr-attr="(item) => ({ id: rowid(g.file, item._row) })" primary-key="_row">
        </b-table>
      </b-tab>
    </b-tabs>

    <!-- Issues -->
    <b-card no-body class="mb-2">
      <b-card-header class="py-1 px-2">
        <strong>{{ t("issues_title") }}</strong>
        <b-badge class="ml-1" variant="secondary">{{ report.issues_total || 0 }}</b-badge>
      </b-card-header>
      <b-card-body class="py-2 px-2">
        <div v-if="!summaryrows.length" class="small text-muted">{{ t("issues_none") }}</div>
        <div v-else>
          <div class="small text-muted mb-1">{{ t("issues_summary_hint") }}</div>
          <b-table small hover striped :dark="$store.state.darkMode" :fields="summaryfields" :items="summaryrows"
            :tbody-tr-class="summaryclass" @row-clicked="summary_clicked" class="mb-2">
            <template #cell(severity)="row">
              <b-badge :variant="severityvariant(row.item.severity)">{{ t("severity_" + row.item.severity) }}</b-badge>
            </template>
            <template #cell(file)="row">{{ row.item.file ? slotname(row.item.file) : "" }}</template>
          </b-table>

          <div class="small text-muted mb-1">
            <span v-if="filtergroup">
              Examples for {{ filtergroup.code }}
              <b-button size="sm" variant="link" class="py-0" @click="filtergroup = null">show all</b-button>
            </span>
            <span v-if="report.issues_truncated">{{ t("issues_detail_hint") }}</span>
          </div>
          <b-table small hover striped sticky-header="360px" :dark="$store.state.darkMode" :fields="detailfields"
            :items="detailrows" @row-clicked="gotorow">
            <template #cell(severity)="row">
              <b-badge :variant="severityvariant(row.item.severity)">{{ t("severity_" + row.item.severity) }}</b-badge>
            </template>
            <template #cell(file)="row">{{ row.item.file ? slotname(row.item.file) : "" }}</template>
            <template #cell(row)="row">{{ row.item.row > 0 ? row.item.row : "" }}</template>
          </b-table>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { text, slotname, ISSUES } from "./ledgerloadercopy";

const ORDER = { ERROR: 0, WARNING: 1, INFO: 2 };

export default {
  name: "LedgerLoaderReport",
  props: {
    // ledgerloader_preview_output or ledgerloader_commit_output
    report: { type: Object, required: true },
    mode: { type: String, default: "preview" },
    flow: { type: Object, default: null },
  },
  data() {
    return {
      activefile: 0,
      filtergroup: null,
      summaryfields: [
        { key: "severity", label: "Severity" },
        { key: "code", label: "Code" },
        { key: "file", label: "File" },
        { key: "column", label: "Column" },
        { key: "count", label: "Count", class: "text-right" },
        { key: "description", label: "What it means" },
      ],
      detailfields: [
        { key: "severity", label: "Severity" },
        { key: "file", label: "File" },
        { key: "row", label: "Row", class: "text-right" },
        { key: "column", label: "Column" },
        { key: "message", label: "Message" },
        { key: "detail", label: "Detail" },
      ],
    };
  },
  computed: {
    issues() {
      return this.report.issues || [];
    },
    hasstopwarning() {
      return (this.report.issue_summary || []).some((i) => i.code === "W_NO_COUNTER_UNIQUE_INDEX");
    },
    counterfile() {
      return (this.report.files || []).find((f) => f.counter_column) || null;
    },
    counterlabel() {
      return this.counterfile ? this.counterfile.counter_column : "BL Counter";
    },
    // file -> Map(row -> worst severity), used to colour grid rows.
    issuesbyrow() {
      const m = new Map();
      this.issues.forEach((i) => {
        if (!i.file || !(i.row > 0)) return;
        if (!m.has(i.file)) m.set(i.file, new Map());
        const rows = m.get(i.file);
        const cur = rows.get(i.row);
        if (cur === undefined || ORDER[i.severity] < ORDER[cur]) rows.set(i.row, i.severity);
      });
      return m;
    },
    // One grid per file, built from the server's rows_json echo (never from the CSV).
    filegrids() {
      return (this.report.files || []).map((f) => {
        const columns = f.columns || [];
        const fields = [{ key: "_row", label: "Row", class: "text-right text-muted" }].concat(
          columns.map((c) => ({ key: c, label: c }))
        );
        let matrix = [];
        try {
          matrix = JSON.parse(f.rows_json || "[]");
        } catch (e) {
          matrix = [];
        }
        const items = matrix.map((cells, i) => {
          const o = { _row: i + 1 };
          columns.forEach((c, j) => {
            o[c] = cells[j];
          });
          const v = this.rowvariant(f.file, i + 1);
          if (v) o._rowVariant = v;
          return o;
        });
        return { file: f.file, report: f, fields, items };
      });
    },
    summaryrows() {
      return (this.report.issue_summary || [])
        .filter((s) => !this.hidden(s.code))
        .map((s) => ({
          ...s,
          severity: this.severityof(s.code, s.file, s.column),
          description: this.describe(s.code),
        }));
    },
    detailrows() {
      let rows = this.issues.filter((i) => !this.hidden(i.code));
      if (this.filtergroup) {
        const g = this.filtergroup;
        rows = rows.filter(
          (i) => i.code === g.code && (i.file || null) === (g.file || null) && (i.column || null) === (g.column || null)
        );
      }
      return rows.sort((a, b) => {
        const d = (ORDER[a.severity] || 0) - (ORDER[b.severity] || 0);
        if (d !== 0) return d;
        return (a.row || 0) - (b.row || 0);
      });
    },
  },
  watch: {
    report() {
      this.activefile = 0;
      this.filtergroup = null;
    },
  },
  methods: {
    t: text,
    slotname,
    // The duplicate banner already says the file set is loaded, so its issue row
    // is left out of the Must fix list; there is nothing in the file to fix.
    hidden(code) {
      return code === "E_DUPLICATE_BATCH" && !!this.report.duplicate_of_batchid;
    },
    when(iso) {
      if (!iso) return "";
      const m = this.$moment(iso);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : iso;
    },
    rowid(file, row) {
      return "ll-" + file + "-" + row;
    },
    rowvariant(file, row) {
      const rows = this.issuesbyrow.get(file);
      if (!rows) return undefined;
      const sev = rows.get(row);
      if (sev === "ERROR") return "danger";
      if (sev === "WARNING") return "warning";
      return undefined;
    },
    severityof(code, file, column) {
      const hit = this.issues.find(
        (i) => i.code === code && (i.file || null) === (file || null) && (i.column || null) === (column || null)
      );
      if (hit && hit.severity) return hit.severity;
      if (String(code).startsWith("W_")) return "WARNING";
      if (String(code).startsWith("I_")) return "INFO";
      return "ERROR";
    },
    severityvariant(sev) {
      if (sev === "ERROR") return "danger";
      if (sev === "WARNING") return "warning";
      return "info";
    },
    describe(code) {
      if (ISSUES[code]) return ISSUES[code];
      const hit = this.issues.find((i) => i.code === code);
      return hit ? hit.message : "";
    },
    summaryclass(item) {
      if (!item || !this.filtergroup) return "";
      const g = this.filtergroup;
      return item.code === g.code && (item.file || null) === (g.file || null) && (item.column || null) === (g.column || null)
        ? "table-active"
        : "";
    },
    summary_clicked(item) {
      const g = this.filtergroup;
      if (g && g.code === item.code && (g.file || null) === (item.file || null) && (g.column || null) === (item.column || null)) {
        this.filtergroup = null;
      } else {
        this.filtergroup = { code: item.code, file: item.file, column: item.column };
      }
    },
    gotorow(issue) {
      if (!issue || !issue.file || !(issue.row > 0)) return;
      const fi = this.filegrids.findIndex((g) => g.file === issue.file);
      if (fi < 0) return;
      this.activefile = fi;
      // BootstrapVue's b-table has no scrollTo; the row carries an id instead.
      const id = this.rowid(issue.file, issue.row);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
      }, 60);
    },
  },
};
</script>

<style></style>
